import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import {
  GET_CATEGORIES_ENUMS,
  GET_SUBCATEGORY_TYPES,
  GET_UNIT_TYPES,
} from 'api/graphql/catalog';
import env from 'config/env';

/**
 * Loads the reference data needed by the Add/Edit product form: product
 * categories, subcategories and unit types. Returns helpers to map them to
 * `<Select>` options and to filter subcategories by the chosen category.
 */
export function useCatalogEnums() {
  const languageCode = env.defaultLanguage;

  const categoriesQuery = useQuery(GET_CATEGORIES_ENUMS, { variables: { languageCode } });
  const subcategoriesQuery = useQuery(GET_SUBCATEGORY_TYPES, { variables: { languageCode } });
  const unitsQuery = useQuery(GET_UNIT_TYPES);

  const categories = useMemo(
    () => categoriesQuery.data?.getProductCategoryTypes ?? [],
    [categoriesQuery.data],
  );
  const subcategories = useMemo(
    () => subcategoriesQuery.data?.getProductSubcategoryTypes ?? [],
    [subcategoriesQuery.data],
  );
  const units = useMemo(() => unitsQuery.data?.getUnitType ?? [], [unitsQuery.data]);

  const categoryOptions = useMemo(
    () => categories.map((item) => ({ value: item.value, label: item.displayValue })),
    [categories],
  );

  const unitOptions = useMemo(
    () =>
      units
        .filter((item) => item.isSupported !== false)
        .map((item) => ({ value: item.value, label: item.displayValue })),
    [units],
  );

  /** Subcategory options filtered to the selected category (by `categoryType`). */
  const getSubcategoryOptions = useMemo(
    () => (categoryValue) =>
      subcategories
        .filter((item) => !categoryValue || item.categoryType === categoryValue)
        .map((item) => ({ value: item.value, label: item.displayValue })),
    [subcategories],
  );

  return {
    categories,
    subcategories,
    units,
    categoryOptions,
    unitOptions,
    getSubcategoryOptions,
    loading: categoriesQuery.loading || subcategoriesQuery.loading || unitsQuery.loading,
  };
}
